import './DatasetPrint.css';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { printOutline } from 'ionicons/icons';
import clsx from 'clsx';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSubsetExportData } from '../../api/hooks/useSubsetExportData/useSubsetExportData';
import Button from '../../components/Button/Button';
import SeptimLoadingSpinner from '../../components/Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useTranslate } from '../../hooks/context/useTranslate';
import useRoutes from '../../hooks/useRoutes';
import { Dataset, DatasetItem, GridRow } from '../../types/types';
import ReportBlocks from './components/ReportBlocks';
import ReportItemsGroups from './components/ReportItemsGroups';
import ReportSheet from './components/ReportSheet';
import { useReportRows } from './hooks/useReportRows';
import { useSheetPagination } from './hooks/useSheetPagination';
import { useStoredReportTemplate } from './hooks/useStoredReportTemplate';
import { useReportAssetUrls } from './hooks/useReportAssetUrls';
import { normalizeColumnWidths } from './utils/normalizeColumnWidths';
import {
  resolvePageGeometry,
  resolvePaginationReserveHeightPx,
} from './utils/resolvePageGeometry';
import { getDefaultTemplateId } from './utils/buildDefaultReportTemplate';
import { resolveReportTemplate } from './utils/resolveReportTemplate';
import { buildReportMeasureKey } from './utils/buildReportMeasureKey';
import {
  buildReportItemGroups,
  resolveItemsDataRows,
} from './utils/resolveItemsDataRows';
import { ReportParentContext, SubsetPrintState } from './types';

type DatasetPrintLocationState = {
  subsetPrint?: SubsetPrintState;
};

/**
 * Renders one report as sheets of paper and hands it to the browser's print
 * dialog.
 *
 * Lives inside myseptim on purpose: it reuses the definition resolution and the
 * grid's formatValue, so a printed report cannot drift from the data on screen.
 * The designer that authors templates is a separate app.
 */
const DatasetPrint: FC = () => {
  const { datasetId, templateId } = useParams<{
    datasetId: string;
    templateId?: string;
  }>();
  const history = useHistory();
  const location = useLocation<DatasetPrintLocationState | undefined>();
  const routes = useRoutes();
  const { t, language } = useTranslate();
  const measureRef = useRef<HTMLDivElement>(null);

  const {
    dataset,
    activeFilters,
    activeSorting,
    datasetData,
    datasetControl,
    slaveDatasets,
    slaveLazyStates,
  } = useDatasetContext();
  const { loadDatasetDefinition } = useSubsetExportData();
  const subsetPrintState = location.state?.subsetPrint;
  const [subsetPrintDataset, setSubsetPrintDataset] = useState<
    Dataset | undefined
  >(undefined);
  const [subsetDefinitionFailed, setSubsetDefinitionFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!subsetPrintState) {
      setSubsetPrintDataset(undefined);
      setSubsetDefinitionFailed(false);
      return () => {
        cancelled = true;
      };
    }

    const cachedSubsetDataset = slaveDatasets[subsetPrintState.subsetId];
    if (cachedSubsetDataset) {
      setSubsetPrintDataset(cachedSubsetDataset);
      setSubsetDefinitionFailed(false);
      return () => {
        cancelled = true;
      };
    }

    void loadDatasetDefinition({
      subsetDatasetId: subsetPrintState.subsetId,
    }).then((loadedDataset) => {
      if (cancelled) return;
      setSubsetPrintDataset(loadedDataset);
      setSubsetDefinitionFailed(!loadedDataset);
    });

    return () => {
      cancelled = true;
    };
  }, [loadDatasetDefinition, slaveDatasets, subsetPrintState]);

  const reportDataset = subsetPrintState ? subsetPrintDataset : dataset;
  const reportActiveFilters = subsetPrintState
    ? (subsetPrintState.activeFilters ?? null)
    : activeFilters;
  const reportActiveSorting = subsetPrintState
    ? (subsetPrintState.activeSorting ?? [])
    : ((activeSorting ?? []) as { field: string; order: number }[]);
  const reportParentContext = useMemo<ReportParentContext | undefined>(() => {
    if (!subsetPrintState || !dataset) return undefined;
    const row =
      subsetPrintState.parentRow ??
      ({
        [subsetPrintState.parentIdItem]: subsetPrintState.parentId,
      } as GridRow);
    return { dataset, row };
  }, [dataset, subsetPrintState]);

  // Stable per mount: every block that stamps the print time shows the same one.
  const printedAt = useMemo(() => new Date(), []);

  /**
   * Which id, if any, has to be fetched. Deliberately undefined until the
   * dataset is known: whether an id is the generated default can only be
   * decided against the dataset, and guessing "not the default" on the first
   * render starts a request for an id that does not exist and leaves the page
   * in a loading state it never asked for.
   */
  const storedTemplateId = useMemo(() => {
    if (!templateId || reportDataset === undefined) {
      return undefined;
    }
    const defaultId = getDefaultTemplateId(
      String(reportDataset.datasetId ?? '')
    );

    // The generated default needs no storage, so it never touches the API.
    return templateId === defaultId ? undefined : templateId;
  }, [templateId, reportDataset]);
  const storedTemplate = useStoredReportTemplate(
    storedTemplateId,
    reportDataset ? String(reportDataset.datasetId ?? '') : undefined
  );

  const resolved = useMemo(
    () =>
      reportDataset
        ? resolveReportTemplate(
            reportDataset,
            templateId,
            storedTemplate.template ? [storedTemplate.template] : [],
            storedTemplate.failed
          )
        : undefined,
    [reportDataset, templateId, storedTemplate.template, storedTemplate.failed]
  );
  const template = resolved?.template;
  const reportAssets = useReportAssetUrls(template);

  const {
    rowsBySource,
    definitionsBySource,
    activeFiltersBySource,
    isLoading,
    error,
    unresolvedSources,
  } = useReportRows({
    template,
    dataset: reportDataset,
    activeFilters: reportActiveFilters,
    activeSorting: reportActiveSorting,
    datasetControl: subsetPrintState ? undefined : datasetControl,
    slaveDatasets,
    slaveLazyStates,
    cachedQueryId: datasetData?.cachedQueryId,
    parentContext: reportParentContext,
  });

  const handleBack = useCallback(() => {
    history.push(subsetPrintState?.returnPath ?? routes.datasetList(datasetId));
  }, [history, routes, datasetId, subsetPrintState?.returnPath]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const items: DatasetItem[] = useMemo(
    () =>
      reportDataset && Array.isArray(reportDataset.items)
        ? reportDataset.items
        : [],
    [reportDataset]
  );

  const geometry = useMemo(
    () =>
      template
        ? resolvePageGeometry(template.page)
        : {
            sheetWidthMm: 0,
            sheetHeightMm: 0,
            printableWidthMm: 0,
            printableHeightMm: 0,
            printableHeightPx: 0,
          },
    [template]
  );

  const itemsDataRows = template
    ? resolveItemsDataRows(template.sections.items)
    : [];
  const primarySource = itemsDataRows[0]?.source ?? 'master';
  const itemRows = rowsBySource[primarySource] ?? [];
  const itemGroups = buildReportItemGroups(rowsBySource);

  const reportMeasureKey = useMemo(
    () =>
      buildReportMeasureKey({
        template,
        itemRows,
        rowsBySource,
        definitionsBySource,
        activeFilters: reportActiveFilters,
        activeFiltersBySource,
        language,
        printedAt,
        sheetWidthMm: geometry.sheetWidthMm,
        sheetHeightMm: geometry.sheetHeightMm,
        isLoading: isLoading || reportAssets.isLoading,
        assetUrls: reportAssets.urls,
      }),
    [
      template,
      itemRows,
      rowsBySource,
      definitionsBySource,
      reportActiveFilters,
      activeFiltersBySource,
      language,
      printedAt,
      geometry.sheetWidthMm,
      geometry.sheetHeightMm,
      isLoading,
      reportAssets.isLoading,
      reportAssets.urls,
    ]
  );

  const { sheets, isMeasuring } = useSheetPagination({
    measureRef,
    // Re-measure whenever the template, the data volume or the paper changes.
    measureKey: reportMeasureKey,
    rowCount: itemGroups.length,
    printableHeightPx: geometry.printableHeightPx,
    paginationReserveHeight: resolvePaginationReserveHeightPx(
      template?.page.marginMm ?? 0
    ),
    hasFooter: Boolean(template?.sections.footer),
  });

  const isBusy =
    isLoading ||
    isMeasuring ||
    storedTemplate.isLoading ||
    reportAssets.isLoading;

  if (subsetDefinitionFailed) {
    return (
      <div className="page-container dataset-print-page">
        <div className="report-toolbar report-screen-only">
          <Button fill="clear" color="dark" round={false} onClick={handleBack}>
            {t('print.back')}
          </Button>
        </div>
        <p className="report-notice report-screen-only">{t('print.error')}</p>
      </div>
    );
  }

  if (!reportDataset || !template) {
    // Nothing to interact with yet, so a bare spinner is the whole page.
    return (
      <div className="report-progress" role="status">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  // Reported once for the whole report, not per sheet.
  const columnsOverflow = itemsDataRows.some(
    (dataRow) => normalizeColumnWidths(dataRow.columns, template.page).overflows
  );

  // Items per source: a block reading a subset resolves labels and formats
  // against that subset's definition, not the master's.
  const itemsBySource = Object.fromEntries(
    Object.entries(definitionsBySource).map(([source, sourceDataset]) => [
      source,
      Array.isArray(sourceDataset.items) ? sourceDataset.items : [],
    ])
  );
  const blocksContext = {
    items,
    itemsBySource,
    rowsBySource,
    datasetLabel: template.label,
    activeFilters: reportActiveFilters,
    activeFiltersBySource,
    printedAt,
    itemRowCount: itemRows.length,
    assetUrls: reportAssets.urls,
  };

  const header = template.sections.header && (
    <header
      className={clsx(
        'report-section report-section-header',
        template.sections.header.border && 'report-section--bordered'
      )}
    >
      <ReportBlocks
        blocks={template.sections.header.blocks}
        defaultSource={template.sections.header.source ?? 'master'}
        {...blocksContext}
      />
    </header>
  );

  const footer = template.sections.footer && (
    <footer
      className={clsx(
        'report-section report-section-footer',
        template.sections.footer.border && 'report-section--bordered'
      )}
    >
      <ReportBlocks
        blocks={template.sections.footer.blocks}
        defaultSource={primarySource}
        {...blocksContext}
      />
    </footer>
  );

  return (
    <div className="page-container dataset-print-page">
      <div className="report-toolbar report-screen-only">
        <Button fill="clear" color="dark" round={false} onClick={handleBack}>
          {t('print.back')}
        </Button>
        <span className="report-toolbar-title">{template.label}</span>
        <Button
          fill="clear"
          color="primary"
          round={false}
          onClick={handlePrint}
          disabled={isBusy || Boolean(error) || reportAssets.failed}
        >
          <IonIcon icon={printOutline} slot="start" />
          {t('print.print')}
        </Button>
      </div>

      {/* Deliberately NOT the full-screen <Loading /> overlay. That one portals a
          backdrop over the whole app and swallows every click, so any one of
          these flags failing to clear would trap the user in a page that looks
          frozen — with the report already rendered underneath it. The Print
          button is disabled while this shows, which is the part that matters. */}
      {isBusy && (
        <div className="report-progress report-screen-only" role="status">
          <SeptimLoadingSpinner />
        </div>
      )}

      {error && (
        <p className="report-notice report-screen-only">{t('print.error')}</p>
      )}

      {reportAssets.failed && (
        <p className="report-notice report-screen-only">{t('print.error')}</p>
      )}

      {resolved.isFallback && (
        <p className="report-notice report-screen-only">
          {t('print.templateFallback')}
        </p>
      )}

      {columnsOverflow && (
        <p className="report-notice report-screen-only">
          {t('print.columnsOverflow')}
        </p>
      )}

      {unresolvedSources.length > 0 && (
        <p className="report-notice report-screen-only">
          {t('print.unresolvedSource')}
        </p>
      )}

      <style>{`@page { size: ${template.page.size} ${template.page.orientation}; margin: 0; }`}</style>

      {/* Measure pass: the whole report at the real printable width, offscreen.
          Row heights have to be read from the DOM — a wrapped value makes a row
          taller and an estimate would break the page in the wrong place. */}
      <div
        className="report-measure"
        ref={measureRef}
        aria-hidden="true"
        style={{ width: `${geometry.printableWidthMm}mm` }}
      >
        {header}
        <ReportItemsGroups
          dataRows={itemsDataRows}
          itemsBySource={itemsBySource}
          groups={itemGroups}
          page={template.page}
        />
        {footer}
      </div>

      {(sheets ?? []).map((sheet, index) => (
        <ReportSheet
          key={index}
          page={template.page}
          geometry={geometry}
          pageNumber={index + 1}
          pageCount={(sheets ?? []).length}
        >
          {header}
          {!sheet.isFooterOnly && (
            <ReportItemsGroups
              dataRows={itemsDataRows}
              itemsBySource={itemsBySource}
              groups={sheet.rowIndexes.map((rowIndex) => itemGroups[rowIndex])}
              page={template.page}
              showEmptyNotice={itemGroups.length === 0}
            />
          )}
          {sheet.hasFooter && footer}
        </ReportSheet>
      ))}
    </div>
  );
};

export default DatasetPrint;
